import { useEffect } from "react";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import { X, Star, MapPin, CheckCircle2, BadgeCheck, ArrowRight, Clock } from "lucide-react";
import { Avatar } from "./Avatar";

/**
 * Profile dialog for a spotlighted "top rated pro" on the public landing page.
 * The pro is curated marketing data passed in directly (no fetch). Since the
 * landing page is only shown to logged-out visitors, the primary CTA nudges
 * them to create a homeowner account via the `force:start-signup` event.
 */
export default function ProProfileDialog({ pro, open, onClose }) {
    useEffect(() => {
        if (!open) return;
        const h = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", h);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", h);
            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    if (!open || !pro) return null;

    const firstName = pro.name?.split(" ")[0] || "this pro";

    function invite() {
        onClose();
        // Nudge the hero AuthWindow into "Create Account" (homeowner) and scroll to it.
        window.dispatchEvent(new CustomEvent("force:start-signup", { detail: { role: "homeowner" } }));
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    return createPortal(
        <div data-testid="pro-profile-dialog" className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto p-4 sm:p-8">
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                role="dialog"
                aria-modal="true"
                aria-label={`${pro.name} — ${pro.specialty} profile`}
                className="glass relative z-10 w-full max-w-md p-6 my-4"
            >
                <button type="button" data-testid="pro-profile-close" aria-label="Close profile" onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-900 dark:hover:text-white">
                    <X className="h-5 w-5" />
                </button>

                <div className="flex items-center gap-4">
                    <div className="relative">
                        <Avatar src={pro.avatar_url} name={pro.name} size="h-16 w-16" rounded="rounded-2xl" textClass="text-xl" testid="pro-profile-avatar" />
                        {pro.verified && (
                            <span className="absolute -bottom-1.5 -right-1.5 grid h-6 w-6 place-items-center rounded-full bg-force-bg">
                                <BadgeCheck className="h-5 w-5 text-orange-500 fill-orange-500/20" />
                            </span>
                        )}
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-display text-2xl font-extrabold tracking-tight truncate">{pro.name}</h3>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-orange-500">{pro.specialty}</p>
                        <span className="mt-0.5 flex items-center gap-1 text-amber-400 text-sm">
                            <Star className="h-4 w-4 fill-amber-400" />{Number(pro.rating).toFixed(1)}
                            <span className="text-zinc-500 dark:text-zinc-500">({pro.rating_count} reviews)</span>
                        </span>
                    </div>
                </div>

                {pro.available_this_week && (
                    <span
                        className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-300"
                        data-testid="pro-dialog-availability"
                    >
                        <Clock className="h-3.5 w-3.5" /> Available this week
                    </span>
                )}

                {pro.bio && <p className="mt-4 text-sm text-zinc-300 dark:text-zinc-300 leading-relaxed">{pro.bio}</p>}

                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                    <div className="rounded-xl border border-white/10 bg-zinc-950/50 p-3">
                        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-zinc-500"><CheckCircle2 className="h-3.5 w-3.5" /> Jobs done</p>
                        <p className="mt-1 font-display text-2xl font-black">{pro.jobs_completed}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-zinc-950/50 p-3">
                        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-zinc-500"><MapPin className="h-3.5 w-3.5" /> Location</p>
                        <p className="mt-1 text-sm text-zinc-200 dark:text-zinc-200 truncate">{pro.city}</p>
                    </div>
                </div>

                {pro.skills?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="pro-skills">
                        {pro.skills.map((s) => (
                            <span key={s} className="rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-xs font-medium text-orange-300 dark:text-orange-300">{s}</span>
                        ))}
                    </div>
                )}

                {pro.reviews?.length > 0 && (
                    <div className="mt-5">
                        <p className="mb-2 text-[11px] uppercase tracking-wide text-zinc-500">Recent reviews</p>
                        <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                            {pro.reviews.map((rv, i) => (
                                <div key={i} data-testid="pro-review" className="rounded-xl border border-white/10 bg-zinc-950/50 p-3">
                                    <div className="flex items-center justify-between">
                                        <span className="inline-flex gap-0.5">
                                            {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= rv.stars ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />)}
                                        </span>
                                        <span className="text-[11px] text-zinc-500">{rv.job_title}</span>
                                    </div>
                                    {rv.comment && <p className="mt-1.5 text-sm text-zinc-200 dark:text-zinc-200">&ldquo;{rv.comment}&rdquo;</p>}
                                    <p className="mt-1 text-[11px] text-zinc-500">— {rv.author}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    data-testid="pro-invite-btn"
                    onClick={invite}
                    className="group mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 via-[#FF7A00] to-amber-500 py-3.5 font-bold text-zinc-950 shadow-[0_8px_30px_rgba(255,122,0,0.38)] transition-[box-shadow,filter] duration-300 hover:shadow-[0_8px_44px_rgba(255,122,0,0.6)] hover:brightness-110"
                >
                    Invite {firstName} to a job
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <p className="mt-2 text-center text-[11px] text-zinc-500">Create a free homeowner account to post a job and invite pros.</p>
            </motion.div>
        </div>,
        document.body
    );
}
